/**
 * Policy Profiles Page — CRUD of runner policy profiles + runner assignment.
 */

import { useState } from 'react';
import { Button, Card, Spinner } from '@/components/ui';
import { Users } from 'lucide-react';
import { PageHeader } from '@vanguard-atlas/shared';
import { Plus, Pencil } from 'lucide-react';
import { useI18n } from '@/i18n';
import { useRunners } from '@/features/mcp';
import { usePolicyProfiles, useGlobalPolicy } from '@/features/mcp/hooks/usePolicyLayers';
import type { PolicyProfile } from '@/features/mcp/hooks/usePolicyLayers';
import { ProfileEditModal } from './components/ProfileEditModal';

const MODE_LABEL: Record<string, string> = {
  explicit_approval: 'Aprobar todo',
  default: 'Por defecto',
  bypass: 'Bypass',
};

export function PolicyProfilesPage() {
  const { t } = useI18n();
  const { data: profiles, isLoading } = usePolicyProfiles();
  const { data: globalConfig } = useGlobalPolicy();
  const { data: runnerList } = useRunners();
  const [editing, setEditing] = useState<PolicyProfile | 'new' | null>(null);

  if (isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Spinner />
      </div>
    );
  }

  const list = profiles ?? [];

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        icon={Users}
        title={t('admin.integrations.policyProfiles') ?? 'Perfiles de política'}
        subtitle="Parametrizaciones comunes heredadas por los runners asignados (global ← perfil ← runner)."
        actions={
          <Button onClick={() => setEditing('new')}>
            <Plus size={16} /> Nuevo perfil
          </Button>
        }
        testId="runners-profiles-header"
      />

      {list.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No hay perfiles. Crea uno para agrupar políticas de runners.
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {list.map((profile) => (
            <Card key={profile.id} className="space-y-2 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-medium">{profile.name ?? profile.id}</h2>
                  <p className="text-xs text-muted-foreground">
                    {MODE_LABEL[profile.config.mode ?? ''] ?? 'Modo heredado'} ·{' '}
                    {(profile.config.allowed_paths ?? []).length} directorios
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setEditing(profile)}>
                  <Pencil size={14} />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {profile.runner_ids.length > 0
                  ? `${profile.runner_ids.length} runner(s) asignado(s)`
                  : 'Sin runners asignados'}
              </p>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <ProfileEditModal
          profile={editing === 'new' ? null : editing}
          runners={runnerList?.runners ?? []}
          initialConfig={editing === 'new' ? globalConfig ?? {} : undefined}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
